import { Loader2 } from 'lucide-react';
import { Panel, compactGhs } from './sales-ui';
import {
  useProspectSourcesQuery,
  type ProspectSourceRow,
} from '@/client/api/prospect/sources.query';
import { SHARE_SITE_COLOR, type ShareSiteId } from '../prospect-share';

// Live per-source scrape data (ff-prospect OLAP) — the real numbers behind every
// site we crawl, so the overview reflects what's actually indexed (and a newly
// onboarded source appears automatically) instead of the static fixture set.

const SOURCE_LABEL: Record<string, string> = {
  jiji: 'Jiji',
  meqasa: 'meQasa',
  gpc: 'GPC',
  lamudi: 'Lamudi',
  seventerra: '7th Terra',
  ghanaweb: 'GhanaWeb',
  realtor: 'Realtor',
  privateproperty: 'Private Property',
  autochek: 'Autochek',
  oxglow: 'Oxglow',
  platform: 'FeelyFeely',
};

const num = (n: number | null | undefined): string => (n ?? 0).toLocaleString();

function SourceRow({ row }: { row: ProspectSourceRow }) {
  const color = row.source in SHARE_SITE_COLOR
    ? SHARE_SITE_COLOR[row.source as ShareSiteId]
    : '#64748b';
  const label = SOURCE_LABEL[row.source] ?? row.source;
  return (
    <tr className="border-t border-gray-100 tabular-nums">
      <td className="py-2 pr-4">
        <span className="inline-flex items-center gap-2">
          <span
            aria-hidden
            className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-sm text-[10px] font-semibold text-white"
            style={{ backgroundColor: color }}
          >
            {label.slice(0, 1).toUpperCase()}
          </span>
          <span className="text-gray-900">{label}</span>
        </span>
      </td>
      <td className="py-2 pr-4 text-right text-gray-900">{num(row.listings)}</td>
      <td className="py-2 pr-4 text-right">{num(row.sellers)}</td>
      <td className="py-2 pr-4 text-right">{num(row.sellers_with_phone)}</td>
      <td className="py-2 pr-4 text-right">{num(row.for_sale)}</td>
      <td className="py-2 pr-4 text-right">{num(row.for_rent)}</td>
      <td className="py-2 pr-4 text-right">{compactGhs(row.inventory_ghs ?? 0)}</td>
      <td className="py-2 pr-4 text-right">{num(row.cross_posted)}</td>
      <td className="py-2 pr-4 text-right text-gray-500">{row.last_scraped ?? '—'}</td>
    </tr>
  );
}

export function LiveSourcesPanel({ country = 'ghana' }: { country?: string }) {
  const q = useProspectSourcesQuery(country);
  const data = q.data;
  const title = `Live scraped sources — ${country.slice(0, 1).toUpperCase()}${country.slice(1)}`;

  return (
    <section className="mb-4">
      <Panel
        title={title}
        actions={
          data ? (
            <span className="text-xs text-gray-500">
              {data.sources.length} sources · {num(data.totals.listings)} listings · {num(data.totals.sellers_with_phone)} contacts
            </span>
          ) : null
        }
      >
        <p className="mb-3 text-xs text-gray-500">
          All-time totals for every source indexed. The share panels below compare FeelyFeely to each
          site over the selected period, so those numbers are scoped differently.
        </p>
        {q.isLoading ? (
          <div className="flex items-center gap-2 py-6 text-sm text-gray-500">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading live source data…
          </div>
        ) : q.isError || !data ? (
          <div className="py-6 text-sm text-gray-500">Live source data unavailable.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-gray-500">
                  <th className="py-2 pr-4 font-medium">Source</th>
                  <th className="py-2 pr-4 text-right font-medium">Listings</th>
                  <th className="py-2 pr-4 text-right font-medium">Sellers</th>
                  <th className="py-2 pr-4 text-right font-medium">With phone</th>
                  <th className="py-2 pr-4 text-right font-medium">For sale</th>
                  <th className="py-2 pr-4 text-right font-medium">For rent</th>
                  <th className="py-2 pr-4 text-right font-medium">Inventory</th>
                  <th className="py-2 pr-4 text-right font-medium">Cross-posted</th>
                  <th className="py-2 pr-4 text-right font-medium">Last scrape</th>
                </tr>
              </thead>
              <tbody>
                {data.sources.map((row) => (
                  <SourceRow key={row.source} row={row} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
    </section>
  );
}
